import { useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ApiError, api, type MeResponse } from '../api/client'
import { accountingApi } from './api'
import { categoryLabel, failedChecks, isInProgress, journalFileName, money, monthLabel, rollForwardRows } from './format'

const REPORTS: [string, string][] = [['summary', 'Summary'], ['cash', 'Cash Report'], ['earned-deferred', 'Earned/Deferred'], ['deferred-by-month', 'Deferred by Month'], ['exceptions', 'Exceptions']]
const btn = 'rounded-lg border border-ink-400 px-3 py-1.5 font-semibold hover:bg-ink-600 disabled:opacity-50'

function errorText(e: unknown): string {
  if (e instanceof ApiError) {
    const d = e.detail as { detail?: unknown }
    return typeof d?.detail === 'string' ? d.detail : JSON.stringify(d?.detail ?? d)
  }
  return 'Something went wrong.'
}

export function ClosePage() {
  const { slug = '', period = '' } = useParams()
  const qc = useQueryClient()
  const { data: me } = useQuery<MeResponse>({ queryKey: ['me'], queryFn: () => api.get<MeResponse>('/api/me') })
  const { data: close, error } = useQuery({ queryKey: ['accounting', 'close', slug, period], queryFn: () => accountingApi.close(slug, period) })
  const [reason, setReason] = useState('')
  const [note, setNote] = useState('')
  const [journalError, setJournalError] = useState('')
  const refresh = () => qc.invalidateQueries({ queryKey: ['accounting'] })
  const act = useMutation({ mutationFn: (fn: () => Promise<unknown>) => fn(), onSuccess: refresh })

  async function downloadJournal() {
    setJournalError('')
    const res = await fetch(accountingApi.journalUrl(slug, period), { credentials: 'include' })
    if (!res.ok) {
      const body = await res.json().catch(() => ({}))
      const unmapped = body?.detail?.unmapped as string[] | undefined
      setJournalError(unmapped ? `Map these accounts first: ${unmapped.join(', ')}` : 'The journal does not balance; it cannot be downloaded.')
      return
    }
    const blob = await res.blob()
    const a = document.createElement('a')
    a.href = URL.createObjectURL(blob)
    a.download = journalFileName(slug, period, close?.status === 'approved')
    a.click()
  }

  if (error) return <p role="alert">Could not load this close.</p>
  if (!close) return <p>Loading…</p>
  const s = close.summary
  const locked = close.status === 'approved'
  const inProgress = isInProgress(close.status)
  const failed = failedChecks(s)
  return (
    <div className="flex flex-col gap-6">
      <div>
        <Link to="/accounting" className="text-paper/60 hover:underline print:hidden">← Monthly closes</Link>
        <h1 className="font-serif text-2xl">{close.name} — {monthLabel(close.period)}</h1>
        <p className="text-paper/70">
          {locked ? `Approved by ${close.approved_by ?? 'someone'} on ${close.approved_at?.slice(0, 10)}. Locked.`
            : inProgress ? 'In progress: live figures so far this month. It can be closed once the month ends.'
            : `Draft, last rebuilt ${close.built_at ?? 'never'}.`}
          {close.takeover && <>{' '}Books held in AAMCirc from {monthLabel(close.takeover)}.</>}
        </p>
        {close.reopened_reason && <p className="text-amber-100">Reopened: {close.reopened_reason}</p>}
        {s.balanced === false && !inProgress && (
          <div role="alert" className="font-semibold text-red-300">
            <p>The close does not balance. It cannot be approved.</p>
            {failed.length > 0 && <ul className="list-disc pl-5 font-normal">{failed.map((f) => <li key={f}>{f}</li>)}</ul>}
          </div>
        )}
      </div>

      <section aria-labelledby="rf" className="rounded-xl border border-ink-500 bg-ink-800 p-4">
        <h2 id="rf" className="font-serif text-lg">Roll-forward</h2>
        <table className="mt-2 w-full max-w-xl">
          <thead><tr className="text-left text-paper/60"><th></th><th className="text-right">Gross</th><th className="text-right">Net</th></tr></thead>
          <tbody>
            {rollForwardRows(s).map((r) => (
              <tr key={r.label} className={r.strong ? 'border-t border-ink-400 font-semibold' : ''}>
                <td className="whitespace-pre py-1">{r.label}</td><td className="text-right tabular-nums">{r.gross}</td><td className="text-right tabular-nums">{r.net}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <p className="mt-3 text-paper/70">Revenue reversed by refunds of already-earned months: {money(s.reversal_gross as string)} gross. Cash in {money(s.cash_in as string)}, refunded {money(s.cash_out as string)}. Late items booked here: {s.late_count ?? 0}{s.late_gross ? ` (${money(s.late_gross)} gross)` : ''}.</p>
        {s.earned_by_category && <p className="text-paper/70">Earned by type: {Object.entries(s.earned_by_category).map(([k, v]) => `${categoryLabel(k)} ${money(v.gross)}`).join(' · ')}</p>}
      </section>

      {close.changes.length > 0 && (
        <section className="rounded-xl border border-ink-500 bg-ink-800 p-4">
          <h2 className="font-serif text-lg">Changed since the previous build</h2>
          <ul className="mt-2">{close.changes.filter((c) => typeof c.after !== 'object').map((c) => <li key={c.key}>{c.key}: {String(c.before)} → {String(c.after)}</li>)}</ul>
        </section>
      )}

      <section className="rounded-xl border border-ink-500 bg-ink-800 p-4">
        <h2 className="font-serif text-lg">Exceptions</h2>
        <ul className="mt-2 text-paper/80">
          <li>Payments with no months recorded: {close.exceptions.no_months.length}</li>
          <li>Refunds whose payment was not found: {close.exceptions.unmatched_refunds.length}</li>
          <li>Late items booked into this month: {close.exceptions.late.length}</li>
          <li>Members whose unearned months differ from their remaining access: {close.exceptions.access_mismatch_total}</li>
          <li>Order lines with no title: {close.exceptions.lines_without_title.length}</li>
        </ul>
        <a className="mt-2 inline-block underline" href={accountingApi.fileUrl(slug, period, 'exceptions', 'csv')}>Download the exceptions list</a>
      </section>

      <section className="rounded-xl border border-ink-500 bg-ink-800 p-4">
        <h2 className="font-serif text-lg">Reports</h2>
        <ul className="mt-2 flex flex-col gap-1">
          {REPORTS.map(([name, label]) => (
            <li key={name}>{label}: <a className="underline" href={accountingApi.fileUrl(slug, period, name, 'csv')}>CSV</a> · <a className="underline" href={accountingApi.fileUrl(slug, period, name, 'xlsx')}>Excel</a></li>
          ))}
        </ul>
        <div className="mt-3 flex flex-wrap gap-2">
          <a className={btn} href={accountingApi.workbookUrl(slug, period)}>Whole close (Excel)</a>
          <Link className={btn} to={`/accounting/${slug}/${period}/print`}>Print / save as PDF</Link>
          <button type="button" className={btn} onClick={downloadJournal}>QuickBooks journal (CSV)</button>
        </div>
        {journalError && <p role="alert" className="mt-2 text-red-300">{journalError} <Link className="underline" to="/accounting/accounts">Account mapping</Link></p>}
      </section>

      <section className="flex flex-col gap-3 rounded-xl border border-ink-500 bg-ink-800 p-4">
        <h2 className="font-serif text-lg">Review</h2>
        {act.error && <p role="alert" className="text-red-300">{errorText(act.error)}</p>}
        {inProgress && <p className="text-paper/70">Nothing to approve yet: this month closes after it ends.</p>}
        {!locked && !inProgress && (
          <div className="flex flex-wrap gap-2">
            <button type="button" className={btn} disabled={act.isPending} onClick={() => act.mutate(() => accountingApi.rebuild(slug, period))}>Rebuild now</button>
            <button type="button" className={`${btn} bg-amber-glow/20`} disabled={act.isPending || s.balanced === false} onClick={() => { if (confirm(`Approve and lock ${monthLabel(period)} for ${close.name}?`)) act.mutate(() => accountingApi.approve(slug, period)) }}>Approve and lock</button>
          </div>
        )}
        {locked && me?.role === 'admin' && (
          <div className="flex flex-wrap items-end gap-2">
            <label className="flex flex-col">Reason to reopen<input className="rounded border border-ink-400 bg-ink-900 px-2 py-1" value={reason} onChange={(e) => setReason(e.target.value)} /></label>
            <button type="button" className={btn} disabled={!reason.trim() || act.isPending} onClick={() => act.mutate(() => accountingApi.reopen(slug, period, reason))}>Reopen</button>
          </div>
        )}
        {!inProgress && <div className="flex flex-wrap items-end gap-2">
          {close.opening_signoff
            ? <p>Opening balance signed off by {close.opening_signoff.by} ({close.opening_signoff.note || 'no note'}).</p>
            : <>
                <label className="flex flex-col">Opening balance sign-off note<input className="rounded border border-ink-400 bg-ink-900 px-2 py-1" value={note} onChange={(e) => setNote(e.target.value)} /></label>
                <button type="button" className={btn} disabled={act.isPending} onClick={() => act.mutate(() => accountingApi.signOff(slug, period, note))}>Sign off opening balance</button>
              </>}
        </div>}
      </section>
    </div>
  )
}
